import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Mic, X } from 'react-feather';
import dynamic from 'next/dynamic';

import fileApi from 'api/fileApi';
import { convertFileAudioOgg, handleToastError } from 'helpers/index';

import styles from 'styles/Unit.module.scss';
import chatStyles from 'styles/Chat.module.scss';

import ButtonSubmit from '../ButtonSubmit';
import ButtonAction from '../ButtonAction';
import { Button, Col, Progress, Row } from 'reactstrap';
import LoadingOverlay from 'react-loading-overlay';

const AudioWithNoSSR = dynamic(() => import('./Audio'), { ssr: false });

const RecordAudio = ({
  type,
  setType,
  onSubmitCompleteUnit,
  setIntervalFunc,
  isDisabled,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [file, setFile] = useState(undefined);
  const [audioFiles, setAudioFiles] = useState([]);
  const [progressOrder, setProgressOrder] = useState(undefined);

  const [recording, setRecording] = useState(false);

  const handleUploadFile = async (file, index) => {
    try {
      setProgressOrder(index);
      const config = {
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent?.loaded * 100) / progressEvent?.total,
          );
          const progress = document.getElementById(
            `progress-upload-file-${index}`,
          )?.firstElementChild;

          if (progress) {
            progress.style.width = `${percentCompleted}%`;
          }
        },
      };

      const formData = new FormData();
      formData.append('users-file', file);
      const fileUploaded = await fileApi.uploadUserFile(formData, config);
      return fileUploaded;
    } catch (error) {
      showToastError('Error while uploading a audio');
    }
  };

  const handleSubmit = async () => {
    try {
      setIsLoading(true);

      const promise = [];
      for (let i = 0; i < audioFiles.length; i++) {
        const item = audioFiles[i];
        let result = await handleUploadFile(item, i);
        promise.push(result);
      }

      const dataFiles = await Promise.all(promise);
      const arrIds = dataFiles.map((item) => item?._id);

      await onSubmitCompleteUnit({ _id: arrIds }, dataFiles);
    } catch (e) {
      handleToastError(e);
    } finally {
      setIsLoading(false);
      setProgressOrder(undefined);
    }
  };

  const removeFile = (index) => {
    const arrFiles = [...audioFiles];
    arrFiles.splice(index, 1);
    setAudioFiles(arrFiles);
  };

  useEffect(() => {
    if (type) {
      setFile(undefined);
    }
    if (audioFiles.length) {
      setAudioFiles([]);
    }
  }, [type]);

  return (
    <LoadingOverlay
      active={isLoading}
      spinner
      styles={{
        overlay: (base) => ({
          ...base,
          background: 'rgb(187 181 181 / 50%)',
        }),
      }}
    >
      <div>
        {type === 'AUDIO' && (
          <>
            <AudioWithNoSSR
              file={file}
              audioFiles={audioFiles}
              setFile={setFile}
              setRecording={setRecording}
              setAudioFiles={setAudioFiles}
            />
            <div className={styles.footerAssigment}>
              <ButtonSubmit
                title="Upload your reply"
                onCancel={() => setType('')}
                disabled={isLoading || !file}
                disabledCancel={isLoading || recording || isDisabled}
                onClick={handleSubmit}
              />
            </div>
          </>
        )}
        {!type && (
          <ButtonAction
            disabled={isDisabled}
            icon={<Mic />}
            text="Record audio"
            onClick={() => {
              setType('AUDIO');
              setIntervalFunc();
            }}
          />
        )}

        {audioFiles?.length ? (
          <div className="my-1">
            <p>Your audio:</p>
            {audioFiles.map((item, index) => (
              <Row className="mx-0" key={index}>
                <Col className="col-12 mb-05">
                  {item?.name}
                  <Button.Ripple
                    className="btn-icon rounded-circle ml-05 px-0 py-0"
                    outline
                    color="secondary"
                    onClick={() => removeFile(index)}
                  >
                    <X size={16} />
                  </Button.Ripple>
                  {progressOrder === index ? (
                    <span
                      className={`pl-05 font-style-italic text-secondary font-small-2 `}
                    >
                      Uploading...
                    </span>
                  ) : null}
                </Col>
                <Col className="col-12">
                  <div className="mb-50">
                    {progressOrder === index ? (
                      <Progress
                        striped
                        id={`progress-upload-file-${index}`}
                        className={`${chatStyles['progress-upload-message']} `}
                      />
                    ) : null}
                  </div>
                </Col>
              </Row>
            ))}
          </div>
        ) : null}
      </div>
    </LoadingOverlay>
  );
};

RecordAudio.propTypes = {
  type: PropTypes.string.isRequired,
  setType: PropTypes.func.isRequired,
  onSubmitCompleteUnit: PropTypes.func.isRequired,
};

export default RecordAudio;
